import { usePatientDiagnosticReports } from '@ctw/shared/api/fhir/diagnostic-report';
import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import type { OverviewFilter } from '@ctw/shared/content/overview/filters';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { useHotloadReady } from '@ctw/shared/hooks/use-hotload-ready';
import { getBffApiBaseUrl } from '@ctw/shared/utils/environments';
import { sort } from '@ctw/shared/utils/sort';
import { compact } from 'lodash-es';
import { useMemo } from 'react';

export const hasA1CCode = (A1CCodes: Array<string>, r: DiagnosticReportModel) =>
  r.loincCode?.code && A1CCodes.includes(r.loincCode.code);

export const containsText = (r: DiagnosticReportModel, text: string) =>
  r.displayName.toLowerCase().includes(text.toLowerCase());

export const getOverviewFilters = (
  A1CCodes: Array<string>,
): Array<OverviewFilter<DiagnosticReportModel>> => [
  {
    key: 'a1c',
    label: 'Filter by A1C',
    description: 'Contains a1c in text or if it includes an a1c code',
    predicate: (r: DiagnosticReportModel) => hasA1CCode(A1CCodes, r) || containsText(r, 'a1c'),
  },
  {
    key: 'cbc',
    label: 'Filter by CBC',
    description: 'Match lowercase “cbc” or “complete blood count” but not containing “cd4/cd8”',
    predicate: (r: DiagnosticReportModel) =>
      /cbc(?!.*cd4\/cd8)|complete blood count/.test(r.displayName.toLowerCase()),
  },
  {
    key: 'cmp',
    label: 'Filter by CMP',
    description: 'Match lowercase “cmp” or “bmp” or “metabolic panel”',
    predicate: (r: DiagnosticReportModel) =>
      ['cmp', 'bmp', 'metabolic panel'].includes(r.displayName.toLowerCase()),
  },
];

type ValueSet = {
  code: string;
  display: string;
};

type ValueSetResource = {
  id: string;
  type: 'value-set';
  attributes: {
    display: string;
    content: Array<ValueSet>;
  };
};

type ValueSetResponse = {
  data: ValueSetResource;
};

const useA1CCodes = () =>
  useCtwQuery<{ content: Array<ValueSet> }>({
    queryId: 'a1cCodes',
    queryFn: async ({ requestContext, ctwFetch }) => {
      const { data } = await ctwFetch<ValueSetResponse>(
        `${getBffApiBaseUrl(requestContext.env)}/v1/terminology/valuesets/a1c`,
        {
          headers: {
            Authorization: `Bearer ${requestContext.authToken}`,
            'content-type': 'application/json',
          },
        },
      );
      return { content: data?.data?.attributes?.content ?? [] };
    },
  });

export interface PatientDiagnosticReportsOverviewData {
  data: Array<DiagnosticReportModel>;
  a1cCodes: Array<string>;
  isLoading: boolean;
  hasNoRecords: boolean;
}

/**
 * Fetches diagnostic reports plus the A1C value-set codes, then merges the
 * most recent report matching each overview filter (A1C / CBC / CMP). Returns
 * the merged list along with `a1cCodes` (needed to clean up A1C display names)
 * and `hasNoRecords` reflecting the raw report fetch.
 */
export function usePatientDiagnosticReportsOverviewData(): PatientDiagnosticReportsOverviewData {
  const { patient } = usePatientContext();
  const ready = useHotloadReady({ upid: patient.UPID, resourceTypes: ['DiagnosticReport'] });
  const { data, isLoading } = usePatientDiagnosticReports(undefined, ready);
  const useA1CCodesResponse = useA1CCodes();

  const a1cCodes = useMemo(
    () => useA1CCodesResponse.data?.content.map((item: ValueSet) => item.code) ?? [],
    [useA1CCodesResponse.data],
  );

  // Derive the merged data directly (no gate on `a1cCodes.length`): the CBC/CMP filters don't
  // depend on the A1C codes, so an empty/failed A1C valueset fetch must not hide those labs. When
  // the codes do arrive, the A1C filter recomputes.
  const mergedData = useMemo(() => {
    const sorted = sort(data ?? [], 'date', 'desc', true) ?? [];
    return compact(
      getOverviewFilters(a1cCodes).map((filter) => sorted.filter(filter.predicate)[0]),
    );
  }, [a1cCodes, data]);

  return {
    data: mergedData,
    a1cCodes,
    isLoading: !ready || isLoading || useA1CCodesResponse.isLoading,
    hasNoRecords: !data || data.length === 0,
  };
}
